import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col } from "reactstrap";
import {
  DataTable,
  IconAvatar,
  StatusBadge,
  PointsValue,
  SidePanelCard,
  GuideSteps,
} from "../../components/Common/PageWidgets";
import SecondaryButton from "../../components/Common/SecondaryButton";

import endpoints from "../../api/endpoints";
import { get } from "../../api/axios";

import copyIcon from "../../assets/images/support/copy.svg";
import shareIcon from "../../assets/images/user-dashboard/share.svg";
import personIcon from "../../assets/images/support/profile-tick.svg";

const REFERRAL_REWARD_POINTS = 1000;

const normalizeText = (value) => value?.toString().trim() || "";

const isTruthy = (value) => value === true || value === 1 || value === "1" || value === "true";

const getReferredUser = (friend) => friend.referred_user || friend.user || friend.referred_member || null;

const isDeletedReferredUser = (friend) => {
  const referredUser = getReferredUser(friend);
  return !referredUser || Boolean(referredUser.deleted_at || friend.deleted_at || friend.is_deleted_user);
};

const isCompletedReferral = (friend) => {
  const status = normalizeText(friend.status || friend.referral_status || friend.effective_status).toLowerCase();
  return status === "completed"
    || isTruthy(friend.is_completed)
    || isTruthy(friend.account_linked)
    || isTruthy(friend.is_linked)
    || Boolean(friend.completed_at || friend.rewarded_at);
};

const getReferralStatus = (friend) => (isCompletedReferral(friend) ? "Completed" : "Pending");

const getInvitedMemberLabel = (friend) => {
  const referredUser = getReferredUser(friend);
  const isDeletedUser = isDeletedReferredUser(friend);
  const fullName = normalizeText(referredUser?.full_name)
    || [referredUser?.first_name, referredUser?.last_name].map(normalizeText).filter(Boolean).join(" ");
  const email = normalizeText(referredUser?.email);
  const invitedEmail = normalizeText(friend.invited_email || friend.email || friend.referred_email);

  if (!isDeletedUser && (fullName || email)) return fullName || email;
  return invitedEmail || "Deleted user";
};

const isReferralAccountLinked = (friend) => {
  if (isTruthy(friend.account_linked) || isTruthy(friend.is_linked)) return true;
  return isCompletedReferral(friend);
};

const getReferralPoints = (friend) => {
  const points = Number(friend.points_awarded ?? friend.points ?? friend.reward_points);
  if (Number.isFinite(points)) return points;
  return isCompletedReferral(friend) ? REFERRAL_REWARD_POINTS : 0;
};

const formatReferralDate = (value) => {
  const rawValue = normalizeText(value);
  if (!rawValue) return "";

  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) return rawValue;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/* -------------------------------------------------------------------------
 * ReferralsPage — referral program hero, impact summary, invited-members
 * table, and a "how to use" guide.
 * ---------------------------------------------------------------------- */

const memberColumns = [
  {
    key: "email",
    label: "Invited Members",
    width: "2fr",
    render: (row) => (
      <span className="data-table__row-label">
        <IconAvatar
          icon={personIcon}
          tone={row.status.toLowerCase() === "accepted" ? "teal" : "orange"}
          size={32}
        />
        {row.email}
      </span>
    ),
  },
  {
    key: "status",
    label: "Status",
    width: "1fr",
    render: (row) => <StatusBadge status={row.status} />,
  },
  {
    key: "account_linked",
    label: "Account Linked",
    width: "1fr",
    render: (row) => (
      <span style={{ 
        color: row.is_deleted_user ? '#ef4444' : row.account_linked ? '#10b981' : '#f59e0b',
        fontWeight: 500 
      }}>
        {row.is_deleted_user ? 'Deleted' : row.account_linked ? 'Linked' : 'Not Linked'}
      </span>
    ),
  },
  {
    key: "points",
    label: "Points",
    width: "0.8fr",
    render: (row) => <PointsValue value={row.points} />,
  },
  { key: "date", label: "Date", width: "1fr", align: "right" },
];

const ReferralsPage = ({
  referral,
  impact,
  members,
  guideSteps,
  onShareLink,
}) => {
  document.title = "Referrals | Omaha Food Lovers";

  const defaultGuideSteps = [
    "Share your unique referral code or link with friends.",
    "They sign up and securely link their first payment card.",
    "You both instantly receive 1,000 points (up to $10 value)!",
  ];

  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState(referral?.code || "");
  const [impactStats, setImpactStats] = useState({
    friendsJoined: impact?.friendsJoined || 0,
    pointsEarned: impact?.pointsEarned || 0,
    pointsGoal: impact?.pointsGoal || 10000,
  });
  const [membersList, setMembersList] = useState(members || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch referral code from API
  useEffect(() => {
    const fetchReferralCode = async () => {
      try {
        const response = await get(endpoints.referralCode);
        const code =
          response.data.data?.code || response.data.data?.referral_code || "";
        if (code) {
          setReferralCode(code);
        }
      } catch (err) {
        console.error("Failed to fetch referral code:", err);
        // Keep the default code from props if API fails
      }
    };

    fetchReferralCode();
  }, []);

  // Fetch referral stats from API
  useEffect(() => {
    const fetchReferralStats = async () => {
      try {
        const response = await get(endpoints.referralStats);
        const stats = response.data.data;
        if (stats) {
          setImpactStats({
            friendsJoined: stats.friends_joined || 0,
            pointsEarned: stats.points_earned || 0,
            pointsGoal: impact?.pointsGoal || 10000,
          });
        }
      } catch (err) {
        console.error("Failed to fetch referral stats:", err);
        // Keep the default stats from props if API fails
      }
    };

    fetchReferralStats();
  }, [impact?.pointsGoal]);

  // Fetch referral friends from API
  const fetchReferralFriends = async (page = 1, isLoadMore = false) => {
    const setLoading = isLoadMore ? setLoadingMore : setIsLoading;
    setLoading(true);
    try {
      const response = await get(`${endpoints.referralFriends}?page=${page}&page_size=16`);
      const paginationData = response.data.data || {};
      const friends = paginationData.data || [];
      const transformedMembers = friends.map((friend) => {
        const referredUser = getReferredUser(friend);
        // Use the formatted date from API if available, otherwise format it locally
        let formattedDate = referredUser?.created_at_formatted || friend.created_at_formatted || "";

        if (!formattedDate && (referredUser?.date_register || friend.date_register)) {
          const dateRegister = referredUser?.date_register || friend.date_register;
          // Fallback: parse date_register format (DD/MM/YY)
          const dateParts = dateRegister.split("/");
          if (dateParts.length === 3) {
            const day = dateParts[0];
            const month = dateParts[1];
            const year = `20${dateParts[2]}`; // Convert YY to YYYY
            const date = new Date(`${year}-${month}-${day}`);
            if (!isNaN(date.getTime())) {
              formattedDate = date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              });
            }
          }
        }

        if (!formattedDate) {
          formattedDate = formatReferralDate(
            friend.completed_at || friend.rewarded_at || referredUser?.created_at || friend.created_at,
          );
        }

        return {
          id: friend.id,
          email: getInvitedMemberLabel(friend),
          is_deleted_user: isDeletedReferredUser(friend),
          status: getReferralStatus(friend),
          account_linked: isReferralAccountLinked(friend),
          points: getReferralPoints(friend),
          date: formattedDate || "N/A",
        };
      });

      if (isLoadMore) {
        setMembersList((prev) => [...prev, ...transformedMembers]);
      } else {
        setMembersList(transformedMembers);
      }

      setCurrentPage(paginationData.current_page || page);
      setHasMorePages(paginationData.current_page < paginationData.last_page);
    } catch (err) {
      console.error("Failed to fetch referral friends:", err);
      // Keep the default members from props if API fails
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchReferralFriends(1, false);
  }, []);

  // Infinite scroll handler
  const handleLoadMoreReferrals = useCallback(() => {
    if (hasMorePages && !loadingMore) {
      fetchReferralFriends(currentPage + 1, true);
    }
  }, [hasMorePages, loadingMore, currentPage]);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // clipboard unavailable — code is still visible to copy manually
    }
  };

  const handleShareLink = async () => {
    const shareUrl = `${window.location.origin}/register?ref=${referralCode}`;
    const shareText = `Join Omaha Food Lovers and get 1000 bonus points! Use my referral code: ${referralCode}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join Omaha Food Lovers",
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }

    if (onShareLink) onShareLink();
  };

  return (
    <div className="page-content">
      <style>{`
        .referrals-empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 24px;
          text-align: center;
          background: var(--bg-white);
          border-radius: var(--radius-xl);
          min-height: 300px;
        }
        .referrals-empty-state__icon {
          font-size: 64px;
          margin-bottom: 16px;
          opacity: 0.6;
        }
        .referrals-empty-state__title {
          font-size: var(--fs-20);
          font-weight: 600;
          color: var(--text-darkest-grey);
          margin: 0 0 12px 0;
        }
        .referrals-empty-state__message {
          font-size: var(--fs-14);
          color: var(--text-dark-grey);
          margin: 0;
          max-width: 400px;
          line-height: 1.5;
        }
      `}</style>
      <Container fluid>
        <Row className="g-3 align-items-stretch" style={{ marginBottom: 24 }}>
          <Col xl={8}>
            <div className="referral-hero-card">
              <div>
                <p className="referral-hero-card__eyebrow">Referral Program</p>
                <h2 className="referral-hero-card__title">
                  {referral?.headline || "Give 1,000 Pts, Get 1,000 Pts"}
                </h2>
                <p className="referral-hero-card__desc">
                  {referral?.description ||
                    "Invite friends to OFL Rewards. When they link their first card, you both get 1,000 points (up to a $10 value) to spend at partner restaurants!"}
                </p>
              </div>
              <div className="referral-hero-card__footer">
                <div className="referral-hero-card__code-group">
                  <span className="referral-hero-card__code-label">
                    Your Referral Code
                  </span>
                  <SecondaryButton
                    label={referralCode}
                    icon={copyIcon}
                    variant="white-transparent"
                    onClick={handleCopyCode}
                    className="referral-hero-card__code-btn"
                  />
                  {copied && <span style={{ fontSize: 13 }}>Copied!</span>}
                </div>
                <SecondaryButton
                  label="Share Link"
                  icon={shareIcon}
                  variant="white"
                  onClick={handleShareLink}
                />
              </div>
            </div>
          </Col>

          <Col xl={4}>
            <div className="impact-card">
              <div>
                <p className="impact-card__eyebrow">Your Impact</p>
                <h2 className="impact-card__title">Friends Joined</h2>
                <p className="impact-card__value">
                  {impactStats.friendsJoined}
                </p>
              </div>
              <div>
                <div className="impact-card__progress-row">
                  <span className="impact-card__progress-label">
                    Points Earned
                  </span>
                  <span className="impact-card__progress-value">
                    {impactStats.pointsEarned.toLocaleString()} pts
                  </span>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-3 align-items-start">
          <Col xl={8}>
            {isLoading ? (
              <DataTable
                columns={memberColumns}
                rows={[]}
                getRowKey={(row) => row.id}
                loading={true}
              />
            ) : membersList.length === 0 ? (
              <div className="referrals-empty-state">
                <div className="referrals-empty-state__icon">👥</div>
                <h3 className="referrals-empty-state__title">No referrals yet</h3>
                <p className="referrals-empty-state__message">
                  Share your referral code with friends to start earning 1,000 points for each successful referral!
                </p>
              </div>
            ) : (
              <DataTable
                columns={memberColumns}
                rows={membersList}
                getRowKey={(row) => row.id}
                loading={isLoading}
                loadingMore={loadingMore}
                onLoadMore={handleLoadMoreReferrals}
                hasMore={hasMorePages}
              />
            )}
          </Col>
          <Col xl={4}>
            <SidePanelCard eyebrow="Guide" title="How to Use">
              <GuideSteps steps={defaultGuideSteps} />
            </SidePanelCard>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

ReferralsPage.propTypes = {
  referral: PropTypes.shape({
    headline: PropTypes.string,
    description: PropTypes.string,
    code: PropTypes.string,
  }),
  impact: PropTypes.shape({
    friendsJoined: PropTypes.number,
    pointsEarned: PropTypes.number,
    pointsGoal: PropTypes.number,
  }),
  members: PropTypes.array,
  guideSteps: PropTypes.arrayOf(PropTypes.string),
  onShareLink: PropTypes.func,
};

export default ReferralsPage;
